import React, { forwardRef, useMemo, useImperativeHandle } from 'react';
import { isString } from 'underscore';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';


// eslint-disable-next-line no-underscore-dangle
declare let _TL_: any;

interface CallToActionTextProps {
  i18n: any;
  defaultValue: string;
}

export const CallToActionText: React.FC<CallToActionTextProps> = forwardRef(({ i18n, defaultValue = '' }: CallToActionTextProps, ref) => {
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const textFieldRef = React.useRef<any>(null); // Create a ref for the TextField
  const [errorMessage, setErrorMessage] = React.useState('');
  const { maxLength }= ResponsiveAd.properties.CallToActionText;

  const getErrorMessage = (newValue) => {
    if (isString(newValue)) {
      if (newValue.trim() === '') {
        return i18n.getString(_TL_('Required field'));
      } else if (newValue.length > maxLength) {
        return i18n.getString(_TL_('Value cannot be longer than 15 characters'));
      }
      return '';
    }
    return '';
  };

  useImperativeHandle(ref, () => ({
    getData: (): string => textFieldRef.current.value.trim(),
    isValid: () => {
      const value = textFieldRef.current.value.trim();
      const message = getErrorMessage(value);
      setErrorMessage(message);
      return message === '';
    },
  }));

  const handleChange = (event, [newValue]) => {
    setErrorMessage(getErrorMessage(newValue));
  };

  const validationSchema = useMemo(() => ({
    type: 'object',
    properties: {
      CallToActionText: {
        ...ResponsiveAd.properties.CallToActionText,
        required: true,
      },
    },
  }), []);

  return (
    <RemainingCharsValidatedTextField
      required
      value={defaultValue}
      i18n={i18n}
      label={i18n.getString(_TL_('Custom action text'))}
      placeholder={i18n.getString(_TL_('Enter custom text'))}
      componentRef={textFieldRef}
      errorMessage={errorMessage}
      onChange={handleChange}
      validationProps={{
        schema: validationSchema,
        field: 'CallToActionText',
        alwaysShowError: true,
        i18n: legacyI18n,
      }}
    />
  );
});

